import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  text-align: center;
`;

const ControlsSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  gap: 2rem;
`;

const ControlSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const PrintButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  
  &:hover {
    background: #0052a3;
    transform: translateY(-2px);
  }
`;

const ConfirmButton = styled.button`
  background: #00aa00;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  
  &:hover {
    background: #008800;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const SongsListSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
`;

const SongsListTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  text-align: center;
`;

const SongsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const SongItem = styled.li`
  color: #cccccc;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  &::before {
    content: '-';
    color: #cc0000;
    font-weight: 700;
    margin-right: 0.5rem;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function QRCodesPrintPage() {
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadSongs();
  }, []);

  const loadSongs = async () => {
    try {
      setLoading(true);
      const allSongs = await songAPI.getSongs('', 'name', 'ASC');
      const qrSongs = allSongs.filter(song => !song.qr_page);
      setSongs(qrSongs);
    } catch (error) {
      console.error('Error loading songs:', error);
      toast.error('Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const songIds = songs.map(song => song.id);
      const response = await songAPI.getMergedQRPDF(songIds);
      
      // Create download link
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QR_Codes_${songs.length}_Songs.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('QR Codes PDF downloaded!');
    } catch (error) {
      console.error('Error generating merged QR PDF:', error);
      toast.error('Failed to generate QR Codes PDF');
    }
  };

  const handleConfirmPrinting = async () => {
    if (songs.length === 0) {
      toast.error('No songs to confirm');
      return;
    }

    try {
      setConfirming(true);
      const songIds = songs.map(song => song.id);
      await songAPI.confirmQRPagesPrinted(songIds);
      toast.success(`Confirmed QR codes printed for ${songs.length} songs!`);
      navigate('/advanced?tab=print');
    } catch (error) {
      console.error('Error confirming QR pages printed:', error);
      toast.error('Failed to confirm printing');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/advanced?tab=print')}>
        ← Back to Print Overview
      </BackButton>

      <Title>QR Codes</Title>

      <ControlsSection>
        <ControlSection>
          <ButtonRow>
            <PrintButton onClick={handlePrint} disabled={songs.length === 0}>
              🖨️ Print
            </PrintButton>
            <ConfirmButton 
              onClick={handleConfirmPrinting}
              disabled={confirming || songs.length === 0}
            >
              {confirming ? 'Confirming...' : '✓ Confirm Printing'}
            </ConfirmButton>
          </ButtonRow>
        </ControlSection>

        <SongsListSection>
          <SongsListTitle>
            QR Codes for {songs.length} {songs.length === 1 ? 'Song' : 'Songs'}:
          </SongsListTitle>
          {songs.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>
              No songs need QR codes printed.
            </p>
          ) : (
            <SongsList>
              {songs.map((song) => (
                <SongItem key={song.id}>{song.name}</SongItem>
              ))}
            </SongsList>
          )}
        </SongsListSection>
      </ControlsSection>
    </PageContainer>
  );
}

export default QRCodesPrintPage;
